import type { VideoQuickCounts } from '@ayub/shared'
import { formatNumber } from '@ayub/shared'
import { CircleCheck, Clock, Hourglass, Link2, TriangleAlert } from '@/lib/icons'
import type { IconType } from '@/lib/icons'
import { cn } from '@/lib/utils'

/**
 * `docs/03-app-flow.md` "Tez filtrlar": `[ 🔴 Kechikkan ] [ 🟡 Bugun ] [ ✅ To'lovga tayyor ]
 * [ Hammasi ]`. Vazifa qo'shimcha ravishda tekshirilmagan havolalar filtrini ham talab qiladi
 * (`docs/03`: "🔗 Tekshirilmagan havolalar: 3") va "havola kutilmoqda" sub-holati uchun chip
 * ko'rib chiqishni tavsiya qiladi (`moderatedAt != null && postedAt == null`, docs/05).
 *
 * `awaiting_link` uchun alohida son yo'q — `VideoQuickCounts` kontraktida (`packages/shared/
 * src/api.ts`) faqat `today`/`overdue`/`ready`/`unverified` bor, shuning uchun bu chip
 * raqamsiz ko'rsatiladi.
 *
 * Bular admin ertalabki ishini SHU YERDAN boshlaydi — shuning uchun bir marta bosish bilan
 * yetadigan, ko'zga tashlanadigan chiplar (44px sensor talabi `h-9` + `px-3` bilan qondiriladi,
 * `flex-wrap` 375px'da ham hammasi ko'rinishini ta'minlaydi).
 */
export type QuickFilter = 'overdue' | 'today' | 'ready' | 'unverified' | 'awaiting_link' | 'all'

interface QuickFilterBarProps {
  value: QuickFilter
  onChange: (value: QuickFilter) => void
  counts: VideoQuickCounts | undefined
  isLoading: boolean
}

export function QuickFilterBar({ value, onChange, counts, isLoading }: QuickFilterBarProps) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label="Tez filtrlar">
      <Chip
        icon={TriangleAlert}
        iconClass="text-urgency-overdue-text"
        label="Kechikkan"
        count={counts?.overdue}
        isLoading={isLoading}
        active={value === 'overdue'}
        onClick={() => onChange('overdue')}
      />
      <Chip
        icon={Clock}
        iconClass="text-urgency-today-text"
        label="Bugun"
        count={counts?.today}
        isLoading={isLoading}
        active={value === 'today'}
        onClick={() => onChange('today')}
      />
      <Chip
        icon={CircleCheck}
        iconClass="text-stage-paid-text"
        label="To'lovga tayyor"
        count={counts?.ready}
        isLoading={isLoading}
        active={value === 'ready'}
        onClick={() => onChange('ready')}
      />
      <Chip
        icon={Link2}
        label="Tekshirilmagan havolalar"
        count={counts?.unverified}
        isLoading={isLoading}
        active={value === 'unverified'}
        onClick={() => onChange('unverified')}
      />
      <Chip
        icon={Hourglass}
        label="Havola kutilmoqda"
        count={undefined}
        isLoading={false}
        active={value === 'awaiting_link'}
        onClick={() => onChange('awaiting_link')}
      />
      <Chip
        label="Hammasi"
        count={undefined}
        isLoading={false}
        active={value === 'all'}
        onClick={() => onChange('all')}
      />
    </div>
  )
}

function Chip({
  icon: Icon,
  iconClass,
  label,
  count,
  isLoading,
  active,
  onClick,
}: {
  icon?: IconType
  /** Ikonka rangi (faqat NOFAOL holatda) — faol chipda butun chip brend rangida bo'ladi. */
  iconClass?: string
  label: string
  count: number | undefined
  isLoading: boolean
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex h-9 items-center gap-1.5 rounded-badge border px-3 text-sm font-medium transition-colors duration-150 ease-out',
        active
          ? 'border-brand bg-brand-subtle text-brand'
          : 'border-app-border text-app-hint hover:text-app-text',
      )}
    >
      {Icon && (
        <Icon size={14} aria-hidden="true" className={cn('shrink-0', !active && iconClass)} />
      )}
      {label}
      {count !== undefined && (
        <span className="tabular text-xs opacity-80">
          {isLoading ? '…' : `(${formatNumber(count)})`}
        </span>
      )}
    </button>
  )
}
